import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase, saveMeme } from '../supabaseClient';

const MemeGenerator = ({ userId }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userMemes, setUserMemes] = useState([]);
  const [topText, setTopText] = useState('');
  const [bottomText, setBottomText] = useState('');
  const [gameReference, setGameReference] = useState('');

  useEffect(() => {
    loadMemeTemplates();
    loadUserMemes();
  }, [userId]);

  const loadMemeTemplates = async () => {
    try {
      const response = await axios.get('https://api.imgflip.com/get_memes');
      setTemplates(response.data.data.memes);
    } catch (error) {
      console.error('Error loading meme templates:', error);
    }
  };

  const loadUserMemes = async () => {
    try {
      const { data, error } = await supabase
        .from('memes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserMemes(data || []);
    } catch (error) {
      console.error('Error loading user memes:', error);
    }
  };

  const generateMeme = async () => {
    if (!selectedTemplate || !topText || !bottomText) {
      alert('Please select a template and enter text');
      return;
    }

    if (!process.env.REACT_APP_IMGFLIP_USERNAME || !process.env.REACT_APP_IMGFLIP_PASSWORD) {
      alert('Imgflip credentials are not properly configured');
      return;
    }

    setLoading(true);
    try {
      // Using Imgflip API to generate meme
      const params = {
        template_id: selectedTemplate.id,
        username: process.env.REACT_APP_IMGFLIP_USERNAME.trim(),
        password: process.env.REACT_APP_IMGFLIP_PASSWORD.trim(),
        text0: topText,
        text1: bottomText
      };

      console.log('Attempting to generate meme with template:', selectedTemplate.id);
      
      // Create URL with parameters
      const url = new URL('https://api.imgflip.com/caption_image');
      Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        }
      });

      const data = await response.json();
      console.log('API Response:', data);

      if (data.success) {
        // Get the URLs of the generated meme
        const memeUrl = data.data.url;
        const pageUrl = data.data.page_url;
        
        try {
          // Save meme URLs to Supabase database
          const { error: insertError } = await supabase
            .from('memes')
            .insert([{
              user_id: userId,
              storage_path: memeUrl,
              page_url: pageUrl,
              game_reference: gameReference,
              created_at: new Date().toISOString()
            }]);

          if (insertError) {
            console.error('Database insert error:', insertError);
            throw new Error(insertError.message);
          }

          await loadUserMemes();
          alert('Meme generated and saved successfully!');
          setTopText('');
          setBottomText('');
          setGameReference('');
          setSelectedTemplate(null);
        } catch (error) {
          console.error('Error saving meme to database:', error);
          alert('Meme was generated but could not be saved to your collection. Please try again.');
        }
      } else {
        throw new Error(data.error_message || 'Failed to generate meme');
      }
    } catch (error) {
      console.error('Error generating meme:', error);
      alert(`Error generating meme: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <style>
        {`
          .memes-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 1.5rem;
            margin-top: 2rem;
          }
          
          .meme-item {
            border: 1px solid var(--xkid-green);
            border-radius: 0.5rem;
            padding: 1rem;
            background-color: var(--xkid-gray-light);
          }
          
          .meme-item img {
            width: 100%;
            height: 300px;
            object-fit: contain;
            border-radius: 0.25rem;
          }
          
          .meme-details {
            margin-top: 1rem;
          }
          
          .meme-details p {
            color: var(--xkid-green);
            font-size: 0.875rem;
            margin: 0.25rem 0;
          }
          
          .meme-links {
            display: flex;
            gap: 1rem;
            margin-top: 0.5rem;
          }
          
          .meme-links a {
            color: var(--xkid-green);
            text-decoration: none;
            font-size: 0.875rem;
          }
          
          .meme-links a:hover {
            text-decoration: underline;
          }
        `}
      </style>
      
      <div className="space-y-6 p-4 bg-xkid-gray rounded-lg border border-xkid-green">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-xkid-green mb-4">Gaming Meme Generator</h2>
          <p className="text-gray-400">Create and share gaming memes!</p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xkid-green mb-2">Top Text</label>
              <input
                type="text"
                value={topText}
                onChange={(e) => setTopText(e.target.value)}
                placeholder="Enter top text..."
                className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
              />
            </div>
            <div>
              <label className="block text-xkid-green mb-2">Bottom Text</label>
              <input
                type="text"
                value={bottomText}
                onChange={(e) => setBottomText(e.target.value)}
                placeholder="Enter bottom text..."
                className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
              />
            </div>
          </div>

          <div>
            <label className="block text-xkid-green mb-2">Game Reference</label>
            <input
              type="text"
              value={gameReference}
              onChange={(e) => setGameReference(e.target.value)}
              placeholder="Enter game name..."
              className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
            />
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplate(template)}
                className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                  selectedTemplate?.id === template.id
                    ? 'border-xkid-green scale-105'
                    : 'border-transparent hover:border-xkid-green'
                }`}
              >
                <img
                  src={template.url}
                  alt={template.name}
                  className="w-full h-32 object-cover"
                />
              </div>
            ))}
          </div>

          <button
            onClick={generateMeme}
            disabled={loading}
            className="w-full p-3 rounded-lg bg-xkid-green text-black font-bold hover:bg-xkid-green-dark transition-colors disabled:opacity-50"
          >
            {loading ? 'Generating...' : 'Generate Meme'}
          </button>
        </div>

        <div className="space-y-4 mt-8">
          <h3 className="text-xl font-bold text-xkid-green">Your Memes</h3>
          <div className="memes-container">
            {userMemes.map((meme) => (
              <div key={meme.id} className="meme-item">
                <img
                  src={meme.storage_path}
                  alt="Generated meme"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/300x300?text=Meme+Load+Error';
                  }}
                />
                <div className="meme-details">
                  <p>Game Reference: {meme.game_reference}</p>
                  <p>Created: {new Date(meme.created_at).toLocaleDateString()}</p>
                  <div className="meme-links">
                    <a href={meme.storage_path} target="_blank" rel="noopener noreferrer">View Image</a>
                    {meme.page_url && (
                      <a href={meme.page_url} target="_blank" rel="noopener noreferrer">View on Imgflip</a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {userMemes.length === 0 && (
            <p className="text-center text-gray-400">No memes generated yet. Create your first one!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemeGenerator; 